import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  Image,
  Video,
  FileText,
  Mail,
  Clipboard,
  X,
  Plus,
  Trash2,
  Wand2,
  Send,
  Shield,
  CheckCircle,
  Loader2,
  FolderUp,
  Link2,
  Zap,
  Globe,
  MessageSquare,
  Megaphone,
  Search,
  Sparkles,
  Eye,
  AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Import source types
const importSources = [
  { id: "upload", label: "Upload", icon: Upload, description: "Photos, videos, files", color: "text-accent-warning" },
  { id: "site", label: "Site", icon: Globe, description: "Import from website", color: "text-primary" },
  { id: "email", label: "Email", icon: Mail, description: "Paste email content", color: "text-blue-400" },
  { id: "text", label: "Text", icon: FileText, description: "Enter text directly", color: "text-green-400" },
  { id: "link", label: "Link", icon: Link2, description: "URL to content", color: "text-accent-neon" },
  { id: "video", label: "Video", icon: Video, description: "Video URL or file", color: "text-red-400" },
  { id: "ad", label: "Ad", icon: Megaphone, description: "Import existing ad", color: "text-purple-400" }
];

// What to do with imported content
const contentActions = [
  { value: "analyze", label: "Analyze", description: "AI analysis of content", icon: Search },
  { value: "create", label: "Create", description: "Generate new content", icon: Sparkles },
  { value: "video", label: "Make Video", description: "Turn into video", icon: Video },
  { value: "broadcast", label: "Broadcast", description: "Send to channels", icon: Send }
];

export default function UploadCenter() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Active import source
  const [activeSource, setActiveSource] = useState("upload");
  
  // Uploaded items
  const [uploadedItems, setUploadedItems] = useState([]);
  const [contentText, setContentText] = useState("");
  const [instructions, setInstructions] = useState("");
  const [selectedAction, setSelectedAction] = useState("create");
  
  // Source inputs
  const [siteUrl, setSiteUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [adUrl, setAdUrl] = useState("");
  const [emailContent, setEmailContent] = useState("");
  
  // Photo source tracking
  const [photoSources, setPhotoSources] = useState({}); // { itemId: 'web' | 'original' | 'generated' }
  
  // Analysis results
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Verification options
  const [wantVerification, setWantVerification] = useState(false);
  const [sourceUrl, setSourceUrl] = useState("");
  
  // Drag state
  const [isDragging, setIsDragging] = useState(false);

  // Handle file upload
  const handleFileUpload = (files, source = 'original') => {
    Array.from(files).forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isDocument = file.type.includes('pdf') || file.type.includes('document');
      
      if (isImage || isVideo || isDocument) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newItem = {
            id: Date.now() + Math.random(),
            file,
            preview: e.target?.result,
            type: isImage ? 'image' : isVideo ? 'video' : 'document',
            name: file.name,
            source: source // 'original', 'web', 'generated'
          };
          setUploadedItems(prev => [...prev, newItem]);
          setPhotoSources(prev => ({ ...prev, [newItem.id]: source }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  // Handle drag & drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files, 'original');
    }
    
    const text = e.dataTransfer.getData('text/plain');
    if (text && !files.length) {
      setContentText(prev => prev + (prev ? '\n\n' : '') + text);
      toast.success("Content added!");
    }
  }, []);

  // Handle paste
  const handlePaste = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            const file = new File([blob], `pasted-image-${Date.now()}.png`, { type });
            handleFileUpload([file], 'original');
            toast.success("Image pasted!");
          }
        }
        
        if (item.types.includes('text/plain')) {
          const blob = await item.getType('text/plain');
          const text = await blob.text();
          if (activeSource === 'email') {
            setEmailContent(prev => prev + (prev ? '\n\n' : '') + text);
          } else {
            setContentText(prev => prev + (prev ? '\n\n' : '') + text);
          }
          toast.success("Text pasted!");
        }
      }
    } catch (err) {
      try {
        const text = await navigator.clipboard.readText();
        if (text) {
          if (activeSource === 'email') {
            setEmailContent(prev => prev + (prev ? '\n\n' : '') + text);
          } else {
            setContentText(prev => prev + (prev ? '\n\n' : '') + text);
          }
          toast.success("Text pasted!");
        }
      } catch (e) {
        toast.error("Unable to access clipboard");
      }
    }
  };

  // Import from URL (site, link, video, ad)
  const importFromUrl = async (url, type) => {
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    
    setAnalyzing(true);
    toast.success(`Importing from ${type}...`);
    
    // Simulate import
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Add placeholder item
    const newItem = {
      id: Date.now(),
      type: type === 'video' ? 'video' : 'link',
      name: url,
      url: url,
      source: 'web',
      preview: null
    };
    setUploadedItems(prev => [...prev, newItem]);
    setPhotoSources(prev => ({ ...prev, [newItem.id]: 'web' }));
    
    toast.success(`Content imported from ${type}!`);
    setAnalyzing(false);
  };

  // Analyze content
  const analyzeContent = async () => {
    if (uploadedItems.length === 0 && !contentText.trim() && !emailContent.trim()) {
      toast.error("Add content to analyze");
      return;
    }
    
    setAnalyzing(true);
    
    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setAnalysisResult({
      summary: "Content analyzed successfully",
      type: uploadedItems.length > 0 ? "Mixed media content" : "Text content",
      sentiment: "Positive",
      keywords: ["product", "sales", "marketing", "quality"],
      suggestions: [
        "Create a 30-second sales video",
        "Generate social media posts",
        "Design an ad campaign"
      ],
      photoSources: Object.entries(photoSources).map(([id, source]) => ({
        id,
        source,
        label: source === 'web' ? '🌐 Web Generated' : source === 'generated' ? '🤖 AI Generated' : '📷 Original'
      }))
    });
    
    setAnalyzing(false);
    toast.success("Analysis complete!");
  };

  // Remove item
  const removeItem = (id) => {
    setUploadedItems(prev => prev.filter(item => item.id !== id));
    setPhotoSources(prev => {
      const newSources = { ...prev };
      delete newSources[id];
      return newSources;
    });
  };

  // Update photo source
  const updatePhotoSource = (id, source) => {
    setPhotoSources(prev => ({ ...prev, [id]: source }));
    setUploadedItems(prev => prev.map(item => 
      item.id === id ? { ...item, source } : item
    ));
  };

  // Process and create
  const processContent = async () => {
    if (uploadedItems.length === 0 && !contentText.trim() && !emailContent.trim()) {
      toast.error("Add content first");
      return;
    }

    setLoading(true);
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success("Content processed!");
    
    setTimeout(() => {
      if (selectedAction === 'video') {
        navigate('/create');
      } else if (selectedAction === 'broadcast') {
        navigate('/broadcast');
      } else if (selectedAction === 'analyze') {
        analyzeContent();
      } else {
        navigate('/library');
      }
    }, 500);
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <span className="font-heading font-bold text-text-primary">Import & Create</span>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="space-y-6">
          
          {/* Hero */}
          <div className="text-center mb-6">
            <h1 className="font-heading font-bold text-2xl text-text-primary mb-2">
              Upload • Import • Analyze • Create
            </h1>
            <p className="text-text-secondary text-sm">
              Import from any source, analyze with AI, and create amazing content
            </p>
          </div>

          {/* Source Selection Tabs */}
          <div className="flex flex-wrap justify-center gap-2 p-2 bg-background-paper rounded-xl border border-border">
            {importSources.map(source => {
              const IconComponent = source.icon;
              const isActive = activeSource === source.id;
              return (
                <button
                  key={source.id}
                  onClick={() => setActiveSource(source.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-primary text-white' 
                      : 'text-text-secondary hover:bg-background-subtle hover:text-text-primary'
                  }`}
                >
                  <IconComponent className={`w-4 h-4 ${isActive ? 'text-white' : source.color}`} />
                  <span className="text-sm font-medium">{source.label}</span>
                </button>
              );
            })}
          </div>

          {/* Source-specific Input */}
          <Card className="bg-background-paper border-border">
            <CardContent className="p-6">
              <AnimatePresence mode="wait">
                {/* Upload Source */}
                {activeSource === 'upload' && (
                  <motion.div
                    key="upload"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      accept="image/*,video/*,.pdf,.doc,.docx"
                      onChange={(e) => handleFileUpload(e.target.files, 'original')}
                      className="hidden"
                    />
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                        isDragging ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Upload className="w-12 h-12 text-text-muted mx-auto mb-3" />
                      <p className="text-text-primary font-medium mb-1">Drag & Drop Files</p>
                      <p className="text-text-muted text-sm mb-4">Photos, videos, documents</p>
                      <div className="flex justify-center gap-2">
                        <Button onClick={() => fileInputRef.current?.click()}>
                          <FolderUp className="w-4 h-4 mr-2" />
                          Browse
                        </Button>
                        <Button variant="outline" onClick={handlePaste}>
                          <Clipboard className="w-4 h-4 mr-2" />
                          Paste
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Site Source */}
                {activeSource === 'site' && (
                  <motion.div
                    key="site"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-5 h-5 text-primary" />
                      <Label className="text-text-primary font-medium">Import from Website</Label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={siteUrl}
                        onChange={(e) => setSiteUrl(e.target.value)}
                        placeholder="https://example.com/product-page"
                        className="flex-1 bg-background border-border"
                      />
                      <Button 
                        onClick={() => importFromUrl(siteUrl, 'site')}
                        disabled={analyzing}
                      >
                        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                        Import
                      </Button>
                    </div>
                    <p className="text-xs text-text-muted">We'll extract images, text, and content from the page</p>
                  </motion.div>
                )}

                {/* Email Source */}
                {activeSource === 'email' && (
                  <motion.div
                    key="email"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-blue-400" />
                        <Label className="text-text-primary font-medium">Paste Email Content</Label>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handlePaste}>
                        <Clipboard className="w-4 h-4 mr-1" />
                        Paste
                      </Button>
                    </div>
                    <Textarea
                      value={emailContent}
                      onChange={(e) => setEmailContent(e.target.value)}
                      placeholder="Paste your email content here... Copy from Gmail, Outlook, or any email client"
                      className="bg-background border-border min-h-[150px]"
                    />
                  </motion.div>
                )}

                {/* Text Source */}
                {activeSource === 'text' && (
                  <motion.div
                    key="text"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-green-400" />
                      <Label className="text-text-primary font-medium">Enter Text Content</Label>
                    </div>
                    <Textarea
                      value={contentText}
                      onChange={(e) => setContentText(e.target.value)}
                      placeholder="Type or paste your content here... Product descriptions, scripts, notes, anything"
                      className="bg-background border-border min-h-[150px]"
                    />
                  </motion.div>
                )}

                {/* Link Source */}
                {activeSource === 'link' && (
                  <motion.div
                    key="link"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="w-5 h-5 text-accent-neon" />
                      <Label className="text-text-primary font-medium">Import from Link</Label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://... (image, document, or content link)"
                        className="flex-1 bg-background border-border"
                      />
                      <Button onClick={() => importFromUrl(linkUrl, 'link')} disabled={analyzing}>
                        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Video Source */}
                {activeSource === 'video' && (
                  <motion.div
                    key="video"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Video className="w-5 h-5 text-red-400" />
                      <Label className="text-text-primary font-medium">Import Video</Label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="YouTube, Vimeo, or direct video URL"
                        className="flex-1 bg-background border-border"
                      />
                      <Button onClick={() => importFromUrl(videoUrl, 'video')} disabled={analyzing}>
                        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
                      </Button>
                    </div>
                    <p className="text-xs text-text-muted">Or upload a video file:</p>
                    <Button variant="outline" onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'video/*';
                      input.onchange = (e) => handleFileUpload(e.target.files, 'original');
                      input.click();
                    }}>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Video File
                    </Button>
                  </motion.div>
                )}

                {/* Ad Source */}
                {activeSource === 'ad' && (
                  <motion.div
                    key="ad"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Megaphone className="w-5 h-5 text-purple-400" />
                      <Label className="text-text-primary font-medium">Import Existing Ad</Label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={adUrl}
                        onChange={(e) => setAdUrl(e.target.value)}
                        placeholder="Facebook Ad URL, Google Ad URL, or landing page"
                        className="flex-1 bg-background border-border"
                      />
                      <Button onClick={() => importFromUrl(adUrl, 'ad')} disabled={analyzing}>
                        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
                      </Button>
                    </div>
                    <p className="text-xs text-text-muted">We'll analyze the ad and help you create variations</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>

          {/* Uploaded Items */}
          {uploadedItems.length > 0 && (
            <Card className="bg-background-paper border-border">
              <CardContent className="p-4">
                <Label className="text-text-primary font-medium mb-3 block">
                  Imported Content ({uploadedItems.length} items)
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {uploadedItems.map((item, index) => (
                    <div key={item.id} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-background-subtle border border-border">
                        {item.type === 'image' && item.preview && (
                          <img src={item.preview} alt="" className="w-full h-full object-cover" />
                        )}
                        {item.type === 'video' && item.preview && (
                          <video src={item.preview} className="w-full h-full object-cover" />
                        )}
                        {(item.type === 'document' || item.type === 'link') && (
                          <div className="w-full h-full flex flex-col items-center justify-center p-2">
                            {item.type === 'link' ? <Link2 className="w-8 h-8 text-text-muted mb-1" /> : <FileText className="w-8 h-8 text-text-muted mb-1" />}
                            <span className="text-xs text-text-muted text-center truncate w-full">{item.name}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Source badge */}
                      <div className={`absolute top-1 left-1 px-2 py-0.5 rounded text-xs font-medium ${
                        item.source === 'web' ? 'bg-blue-500/80 text-white' :
                        item.source === 'generated' ? 'bg-purple-500/80 text-white' :
                        'bg-green-500/80 text-white'
                      }`}>
                        {item.source === 'web' ? '🌐 Web' : item.source === 'generated' ? '🤖 AI' : '📷 Original'}
                      </div>
                      
                      {/* Order number */}
                      <div className="absolute bottom-1 left-1 w-5 h-5 rounded-full bg-black/70 text-white text-xs flex items-center justify-center">
                        {index + 1}
                      </div>
                      
                      {/* Remove & Source selector */}
                      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <select
                          value={photoSources[item.id] || 'original'}
                          onChange={(e) => updatePhotoSource(item.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs bg-black/70 text-white rounded px-1 py-0.5 border-0"
                        >
                          <option value="original">📷 Original</option>
                          <option value="web">🌐 Web</option>
                          <option value="generated">🤖 AI Gen</option>
                        </select>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="w-6 h-6 rounded bg-red-500 text-white flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add more */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center text-text-muted hover:text-primary"
                  >
                    <Plus className="w-6 h-6 mb-1" />
                    <span className="text-xs">Add More</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Instructions */}
          <Card className="bg-background-paper border-border">
            <CardContent className="p-4">
              <Label className="text-text-primary font-medium flex items-center gap-2 mb-2">
                <Wand2 className="w-4 h-4 text-accent-neon" />
                Instructions (What do you want to create?)
              </Label>
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Example: Create a 30-second sales video from these photos..."
                className="bg-background border-border min-h-[80px]"
              />
            </CardContent>
          </Card>

          {/* Action Selection */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {contentActions.map((action) => {
              const IconComponent = action.icon;
              const isSelected = selectedAction === action.value;
              return (
                <Card
                  key={action.value}
                  onClick={() => setSelectedAction(action.value)}
                  className={`cursor-pointer transition-all ${
                    isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                  }`}
                >
                  <CardContent className="p-4 text-center">
                    <IconComponent className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-primary' : 'text-text-muted'}`} />
                    <p className={`font-medium text-sm ${isSelected ? 'text-primary' : 'text-text-primary'}`}>{action.label}</p>
                    <p className="text-xs text-text-muted">{action.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Analysis Result */}
          {analysisResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-accent-success/10 border-accent-success/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-accent-success" />
                    <span className="font-medium text-text-primary">Analysis Complete</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-text-muted">Type: <span className="text-text-primary">{analysisResult.type}</span></p>
                      <p className="text-text-muted">Sentiment: <span className="text-text-primary">{analysisResult.sentiment}</span></p>
                      <p className="text-text-muted">Keywords: <span className="text-text-primary">{analysisResult.keywords.join(', ')}</span></p>
                    </div>
                    <div>
                      <p className="text-text-muted mb-1">Photo Sources:</p>
                      {analysisResult.photoSources.map(ps => (
                        <span key={ps.id} className="inline-block mr-2 mb-1 px-2 py-0.5 rounded bg-background text-xs">
                          {ps.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-accent-success/20">
                    <p className="text-text-muted text-sm mb-2">Suggestions:</p>
                    <div className="flex flex-wrap gap-2">
                      {analysisResult.suggestions.map((s, i) => (
                        <Button key={i} size="sm" variant="outline" className="text-xs">
                          {s}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SelleSafe Verification */}
          <Card className="bg-background-paper border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-accent-success" />
                  <div>
                    <p className="font-medium text-text-primary text-sm">SelleSafe Verification</p>
                    <p className="text-xs text-text-muted">Optional - Track content source on blockchain</p>
                  </div>
                </div>
                <Switch checked={wantVerification} onCheckedChange={setWantVerification} />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={analyzeContent}
              disabled={analyzing}
              variant="outline"
              className="flex-1 py-6"
            >
              {analyzing ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Search className="w-5 h-5 mr-2" />
              )}
              Analyze Content
            </Button>
            <Button
              onClick={processContent}
              disabled={loading}
              className="flex-1 bg-accent-warning hover:bg-accent-warning/90 text-black py-6 font-bold"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Zap className="w-5 h-5 mr-2" />
              )}
              {selectedAction === 'analyze' ? 'Analyze' : selectedAction === 'create' ? 'Create Content' : selectedAction === 'video' ? 'Make Video' : 'Broadcast'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
